import { StorageAPI } from 'clients';
import { ImmutableModelStore } from 'solox';
import { PERSISTENT_PREFIX } from 'src/core/utils/StorageAPI';
import { PUBLIC_STORED_CONTROLLER_STATES, STORED_CONTROLLER_STATES } from 'vd-constants';
export interface VideoPlayerOptionsState {
  readonly truncateEvents?: boolean;
  /** If true, video player will use pinhole for 360 cameras */
  readonly showPinholeView?: boolean;
  readonly followingPrimaryCameraId?: string | undefined | null;
  readonly lastLinkedCameraId?: string | undefined | null;
  readonly linkColors?: any;
  /** The ID of the "expanded" player - this player will be shown larger than the others. */
  readonly expandedPlayerId?: string | undefined;
  readonly cameraLinkingEditMode?: boolean;
  /** True if we should display the receipt overlaying the video player */
  readonly showReceipt: boolean;
  /** True if we should load thumbnails when the user hovers over the timeline. */
  readonly previewThumbnailsOn: boolean;
  /** True if we should declutter markers on the timeline. */
  readonly declutterMarkersOn: boolean;
  /**
   * True if the Line/Zone Overlay is on: draws the active event's geometry
   * (zone polygon for zone-tracking events, crossing line for line-crossing
   * events) only — not object bounding boxes.
   */
  readonly showZoneTrackingEventZone: boolean;
  /** True if we want to draw bounding boxes around zone tracking objects */
  readonly showBoundingBoxes: boolean;
  /** True if we should show door controls. */
  readonly showDoorControls: boolean;
}

/**
 * VideoPlayerOptionsController is a controller used to manage the options of the video player.
 *
 * For authenticated users, these options in this controller should automatically persisted to local storage,
 * and will survive page reloads but will clear when user logins.
 *
 * For public routes, these options are persisted to local storage with a different key,
 * and will be persistent until user clears their local storage.
 */

export class VideoPlayerOptionsController {
  public state: ImmutableModelStore<VideoPlayerOptionsState>;

  constructor(authenticated: boolean) {
    const localStorageKey = authenticated
      ? STORED_CONTROLLER_STATES
      : PERSISTENT_PREFIX + PUBLIC_STORED_CONTROLLER_STATES;
    const stored = StorageAPI.getLocally(localStorageKey) as any | null;
    if (stored?.videoPlayerOptions) {
      this.state = new ImmutableModelStore<VideoPlayerOptionsState>({
        ...stored.videoPlayerOptions,
        // Default to ON for returning users whose persisted state predates this option.
        showBoundingBoxes: stored.videoPlayerOptions.showBoundingBoxes ?? true,
      });
    } else {
      this.state = new ImmutableModelStore<VideoPlayerOptionsState>({
        truncateEvents: false,
        showPinholeView: false,
        followingPrimaryCameraId: null,
        lastLinkedCameraId: null,
        linkColors: {},
        expandedPlayerId: undefined,
        cameraLinkingEditMode: false,
        showReceipt: true,
        previewThumbnailsOn: true,
        declutterMarkersOn: true,
        showZoneTrackingEventZone: false,
        showBoundingBoxes: true,
        showDoorControls: false,
      });
    }

    this.state.subscribe(() => {
      StorageAPI.storeLocally(localStorageKey, { videoPlayerOptions: this.getState() });
    });
  }

  getState(): VideoPlayerOptionsState {
    return this.state.current;
  }

  toggleTruncateEvents(truncate?: boolean) {
    this.state.update((draft) => {
      draft.truncateEvents = truncate ?? !draft.truncateEvents;
    });
  }

  toggleShowPinholeView(value?: boolean) {
    this.state.update((draft) => {
      if (value !== undefined) {
        draft.showPinholeView = value;
      } else {
        draft.showPinholeView = !draft.showPinholeView;
      }
    });
  }

  setFollowingPrimaryCameraId(cameraId: string | undefined | null) {
    this.state.update((draft) => {
      draft.followingPrimaryCameraId = cameraId;
    });
  }

  setLastLinkedCameraId(cameraId: string | undefined | null) {
    this.state.update((draft) => {
      draft.lastLinkedCameraId = cameraId;
    });
  }

  resetLinkColor() {
    this.state.update((draft) => {
      draft.linkColors = {};
    });
  }

  setLinkColor(link: string, color: string) {
    this.state.update((draft) => {
      draft.linkColors[link] = color;
    });
  }

  setExpandedPlayerId(playerId: string | undefined) {
    this.state.update((draft) => {
      draft.expandedPlayerId = playerId;
    });
  }

  setCameraLinkingEditMode(value: boolean) {
    this.state.update((draft) => {
      draft.cameraLinkingEditMode = value;
    });
  }

  toggleShowReceipt(value?: boolean) {
    this.state.update((draft) => {
      draft.showReceipt = value ?? !draft.showReceipt;
    });
  }

  togglePreviewThumbnails(value?: boolean) {
    this.state.update((draft) => {
      draft.previewThumbnailsOn = value ?? !draft.previewThumbnailsOn;
    });
  }

  toggleDeclutterMarkers(value?: boolean) {
    this.state.update((draft) => {
      draft.declutterMarkersOn = value ?? !draft.declutterMarkersOn;
    });
  }

  toggleShowZoneTrackingZone(value?: boolean) {
    this.state.update((draft) => {
      draft.showZoneTrackingEventZone = value ?? !draft.showZoneTrackingEventZone;
    });
  }

  toggleShowBoundingBoxes(value?: boolean) {
    this.state.update((draft) => {
      draft.showBoundingBoxes = value ?? !draft.showBoundingBoxes;
    });
  }

  toggleShowDoorControls(value?: boolean) {
    this.state.update((draft) => {
      draft.showDoorControls = value ?? !draft.showDoorControls;
    });
  }
}
